import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { CheckIcon, ImageIcon, StackIcon } from '@radix-ui/react-icons'
import { ThumbsUp } from 'lucide-react'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { cptThumb } from 'commons/cdn'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import { useBatchSelectStore } from '../batchSelectStore'
import { CptCard } from '../ui'

// import { Badge } from '@/components/ui/badge'
import DataItemActions from '../data-item-actions'
import CptProSelector from './CptProSelector'
import { isDeletedComponent } from 'Editor/lib/visibleComponents'

const Cpt = ({ cpt, cpts, itemRef, itemStyle, isDragging, dragHandleProps }) => {
  const { id } = cpt

  const childs = useMemo(
    () => cpts?.filter((c) => c.pid === id && !isDeletedComponent(c)),
    [id, cpts],
  )

  if (isDeletedComponent(cpt)) return null
  if (cpt.pid) return null
  if (childs?.length > 0) {
    return (
      <CptStack
        cpt={cpt}
        cpts={cpts}
        childs={childs}
        itemRef={itemRef}
        itemStyle={itemStyle}
        isDragging={isDragging}
        dragHandleProps={dragHandleProps}
      />
    )
  }

  return (
    <CptNormal
      cpt={cpt}
      cpts={cpts}
      itemRef={itemRef}
      itemStyle={itemStyle}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
    />
  )
}

const RecommendBadge = ({ cpt }) => {
  if (Number(cpt?.recommend) !== 1) return null
  return (
    <ThumbsUp className="absolute bottom-1 left-1 z-10 h-4 w-4 fill-yellow-400 text-yellow-400 drop-shadow" />
  )
}

const BatchCheckMark = ({ id }) => {
  const enabled = useBatchSelectStore((s) => s.enabled)
  const selected = useBatchSelectStore((s) => s.selectedIds.includes(id))
  if (!enabled) return null
  return (
    <div
      className={cn(
        'absolute top-1 right-1 z-10 flex h-5 w-5 items-center justify-center rounded border',
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-muted-foreground/40 bg-white/90',
      )}
    >
      {selected ? <CheckIcon className="h-3.5 w-3.5" /> : null}
    </div>
  )
}

const CptNormal = ({
  cpt,
  cpts,
  childs,
  itemRef,
  itemStyle,
  isDragging,
  dragHandleProps,
}) => {
  const { tid } = useParams()
  const { id, cover } = cpt

  const batchEnabled = useBatchSelectStore((s) => s.enabled)
  const batchSelected = useBatchSelectStore((s) =>
    s.selectedIds.includes(id),
  )
  const toggleBatch = useBatchSelectStore((s) => s.toggle)

  const selected = useEditorStore((state) =>
    state.editors[tid]?.drawers?.some((d) => d.cid === id),
  )
  const selectCpt = useEditorStore((state) => state.selectCpt)
  const deselectCpt = useEditorStore((state) => state.deselectCpt)

  const { data: layers } = useSWR(['/layers', { tid }])

  const handleClick = () => {
    if (batchEnabled) {
      toggleBatch(cpt.id)
      return
    }
    let layer = layers?.find((layer) => layer.id === cpt.lid)
    if (!layer) layer = { id: 'draft', max: 1 }
    if (selected) {
      deselectCpt(tid, cpt.id)
    } else {
      selectCpt(tid, layer, cpt)
    }
  }

  return (
    <CptCard
      ref={itemRef}
      style={itemStyle}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      onClick={handleClick}
      className={cn(
        (batchEnabled ? batchSelected : selected) && 'border-2 border-primary',
      )}
      children2={
        batchEnabled ? null : (
          <div className="mt-2 flex w-full items-center gap-1">
            <CptProSelector cpt={cpt} />
            <DataItemActions cpt={cpt} cpts={cpts} />
          </div>
        )
      }
    >
      <div className="w-full h-full relative">
        <img
          className="w-full h-full aspect-square object-contain rounded-xl"
          src={cptThumb(cover ?? id)}
          alt=""
        />
        <BatchCheckMark id={id} />
        <RecommendBadge cpt={cpt} />
        {cpt.cover && (
          <ImageIcon className="w-4 h-4 text-primary absolute top-1 left-1" />
        )}
      </div>
    </CptCard>
  )
}

const CptStack = ({
  cpt,
  cpts,
  childs,
  itemRef,
  itemStyle,
  isDragging,
  dragHandleProps,
}) => {
  const { tid } = useParams()
  const { id, cover } = cpt

  const batchEnabled = useBatchSelectStore((s) => s.enabled)
  const batchSelected = useBatchSelectStore((s) =>
    s.selectedIds.includes(id),
  )
  const toggleBatch = useBatchSelectStore((s) => s.toggle)

  const selected = useEditorStore((state) =>
    state.editors[tid]?.drawers?.some((d) => d.cid === id || d.pid === id),
  )
  const [open, setOpen] = useState(false)
  const justDragged = useRef(false)

  useEffect(() => {
    if (isDragging) {
      justDragged.current = true
      setOpen(false)
    }
  }, [isDragging])

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (justDragged.current) {
          justDragged.current = false
          setOpen(false)
          return
        }
        setOpen(next)
      }}
    >
      <CptCard
        ref={itemRef}
        style={itemStyle}
        isDragging={isDragging}
        dragHandleProps={dragHandleProps}
        onClick={batchEnabled ? () => toggleBatch(id) : undefined}
        className={cn(
          (batchEnabled ? batchSelected : selected) &&
            'border-2 border-primary',
        )}
      >
        <div className="w-full h-full relative">
          {batchEnabled ? (
            <>
              <img
                className="w-full h-full aspect-square object-contain rounded-xl"
                src={cptThumb(cover ?? id)}
                alt=""
              />
              <BatchCheckMark id={id} />
              <RecommendBadge cpt={cpt} />
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="absolute bottom-1 right-2 flex items-center text-primary gap-0.5 bg-gray-100 rounded-md px-1 hover:bg-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <StackIcon className="w-4 h-4 " />
                  {childs.length + 1}
                </button>
              </PopoverTrigger>
            </>
          ) : (
            <PopoverTrigger asChild>
              <div className="w-full h-full relative cursor-pointer">
                <img
                  className="w-full h-full aspect-square object-contain rounded-xl"
                  src={cptThumb(cover ?? id)}
                  alt=""
                />
                <RecommendBadge cpt={cpt} />
                <div className="absolute top-1 right-2 flex items-center text-primary gap-0.5 bg-gray-100 rounded-md px-1 ">
                  <StackIcon className="w-4 h-4 " />
                  {childs.length + 1}
                </div>
              </div>
            </PopoverTrigger>
          )}
        </div>
      </CptCard>
      <PopoverContent className="max-h-[80vh] min-w-[40vw] xl:min-w-[30vw]">
        <div className="overflow-y-auto">
          <div className="grid grid-cols-6 lg:grid-cols-5 xl:grid-cols-5 gap-2 p-1 ">
            <CptNormal cpt={cpt} cpts={cpts} childs={childs} />
            {childs.map((child) => (
              <CptNormal key={child.id} cpt={child} cpts={cpts} />
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default Cpt
